import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { HelmetProvider } from 'react-helmet-async';
import App from './App.tsx';
import { ErrorBoundary } from './components/ErrorBoundary.tsx';
// Latin-only subsets: the full @fontsource CSS ships 7 @font-face blocks per
// weight (latin, latin-ext, cyrillic(-ext), greek(-ext), vietnamese). A
// Swedish/English site renders none of the non-latin scripts, so the extra
// rules were dead CSS and the browser could speculatively fetch woff2s we
// never draw. `latin-<weight>.css` is one @font-face each.
import '@fontsource/inter/latin-400.css';
import '@fontsource/inter/latin-500.css';
import '@fontsource/inter/latin-600.css';
import '@fontsource/inter/latin-700.css';
import '@fontsource/inter/latin-800.css';
import '@fontsource/lora/latin-500.css';
import '@fontsource/lora/latin-600.css';
import '@fontsource/lora/latin-700.css';
import './index.css';
import './lib/i18n';

// From ./lib/supabaseConfig, not ./lib/supabase: importing the config here
// must not drag the Supabase client bundle into the entry chunk.
import { isSupabaseConfigured } from './lib/supabaseConfig';
import {
  applyDesignTemplate,
  isThemedPath,
  readCachedTemplateId,
} from './lib/designTemplates';

// Apply the last known workspace design template before the first paint.
// Deliberately here and not in a component effect: an effect runs *after* the
// first paint, which is exactly when the flash of the default palette would be
// visible. DesignTemplateLoader reconciles this against the database once the
// session is known.
if (isThemedPath(window.location.pathname)) {
  applyDesignTemplate(readCachedTemplateId());
}

const rootElement = document.getElementById('root')!;

if (!isSupabaseConfigured) {
  createRoot(rootElement).render(
    <StrictMode>
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        fontFamily: 'sans-serif',
        backgroundColor: '#faf7f0',
        color: '#2e5940',
        padding: '20px',
        textAlign: 'center'
      }}>
        <div style={{
          backgroundColor: '#fff',
          padding: '40px',
          borderRadius: '16px',
          boxShadow: '0 4px 30px rgba(0,0,0,0.05)',
          maxWidth: '500px',
          border: '1px solid #4f7a5c'
        }}>
          <h1 style={{ fontSize: '24px', marginBottom: '16px', fontWeight: 'bold' }}>Konfigurationsfel</h1>
          <p style={{ fontSize: '15px', color: '#4f7a5c', lineHeight: '1.6', marginBottom: '24px' }}>
            Applikationen kunde inte starta eftersom miljövariablerna för databasen (Supabase) saknas i produktionsmiljön.
          </p>
          <div style={{
            textAlign: 'left',
            backgroundColor: '#f3f1eb',
            padding: '16px',
            borderRadius: '8px',
            fontFamily: 'monospace',
            fontSize: '13px',
            color: '#c2410c',
            marginBottom: '24px'
          }}>
            VITE_SUPABASE_URL<br />
            VITE_SUPABASE_ANON_KEY
          </div>
          <p style={{ fontSize: '13px', color: '#6b7280' }}>
            Lägg till dessa som build-variabler i Coolify (motsvarande <code>VITE_SUPABASE_URL</code>/<code>VITE_SUPABASE_ANON_KEY</code> i <strong>docker-compose.yml</strong>) — de bakas in vid byggtiden, inte vid start.
          </p>
        </div>
      </div>
    </StrictMode>
  );
} else {
  createRoot(rootElement).render(
    <StrictMode>
      <HelmetProvider>
        <ErrorBoundary>
          <App />
        </ErrorBoundary>
      </HelmetProvider>
    </StrictMode>
  );
}
